/**
 * PATH: apps/qa/src/components/inspection/JobContextCard.tsx
 * PURPOSE: Display job context information in inspection creation flow
 * 
 * WHY: Users need constant visibility of which job they're creating an
 * inspection for. This component provides that context in both full and
 * compact variants.
 * 
 * VARIANTS:
 * - Full: Larger card with avatar, job ID, client, site, type, and tags
 * - Compact: Smaller inline badge for headers
 * 
 * UPDATED: Now supports Site/Client objects and displays job tags for AI context
 */

import { Building2, Sparkles } from 'lucide-react'

interface Site {
  id?: number
  name?: string
  address?: string
}

interface Client {
  id?: number
  name?: string
}

interface Job {
  job_id: number
  quotech_job_id?: number | null
  status?: string | null
  combined_status?: string | null
  type?: string | null  // 'commercial' | 'civil' | 'remediation' | 'basement'
  site?: Site | string | null
  client?: Client | string | null
  project_manager?: string | null
  tags?: string[]  // Array of tag names from backend
}

interface JobContextCardProps {
  job: Job | null
  compact?: boolean
}

/**
 * Gets display name for site (handles both string and object formats)
 */
function getSiteName(site: Site | string | null | undefined): string {
  if (!site) return ''
  if (typeof site === 'string') return site
  return site.name || site.address || ''
}

/**
 * Gets display name for client (handles both string and object formats)
 */
function getClientName(client: Client | string | null | undefined): string {
  if (!client) return ''
  if (typeof client === 'string') return client
  return client.name || ''
}

export function JobContextCard({ job, compact = false }: JobContextCardProps) {
  if (!job) return null
  
  const siteName = getSiteName(job.site)
  const clientName = getClientName(job.client)
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-sm">
        <Building2 className="w-4 h-4 text-primary" />
        <span className="font-medium text-foreground">Job #{job.job_id}</span>
        {job.type && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary capitalize">
            {job.type}
          </span>
        )}
        {siteName && <span className="text-muted-foreground">• {siteName}</span>}
      </div>
    )
  }
  
  return (
    <div className="p-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">
            Job #{job.job_id}
            {job.type && (
              <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/20 text-primary capitalize">
                {job.type}
              </span>
            )}
          </p>
          <p className="text-sm text-muted-foreground truncate">
            {clientName || 'No client'} • {siteName || 'No site'}
          </p>
        </div>
      </div>
      {/* Show job tags if available */}
      {job.tags && job.tags.length > 0 && (
        <div className="mt-2 pt-2 border-t border-primary/10">
          <div className="flex items-center gap-1 flex-wrap">
            <Sparkles className="w-3 h-3 text-amber-500" />
            {job.tags.slice(0, 4).map((tag, idx) => (
              <span key={idx} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300">
                {tag}
              </span>
            ))}
            {job.tags.length > 4 && (
              <span className="text-[10px] text-muted-foreground">+{job.tags.length - 4} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default JobContextCard
